import { memo } from 'react'
import { Handle, Position } from '@xyflow/react'
import { Play, Clock, Webhook, FileText, Zap } from 'lucide-react'
import { cn } from '@/lib/utils'

export type TriggerType = 'manual' | 'schedule' | 'webhook' | 'form' | 'event'

export interface TriggerShapeNodeData {
  label: string
  triggerType: TriggerType
  status?: 'idle' | 'active' | 'triggered'
  scheduleText?: string
  cronExpression?: string
  webhookUrl?: string
  formId?: string
  onDelete?: () => void
  [key: string]: unknown
}

const triggerConfig: Record<TriggerType, { icon: typeof Play; color: string; bgColor: string }> = {
  manual: { icon: Play, color: '#3b82f6', bgColor: 'rgba(59, 130, 246, 0.15)' },
  schedule: { icon: Clock, color: '#8b5cf6', bgColor: 'rgba(139, 92, 246, 0.15)' },
  webhook: { icon: Webhook, color: '#06b6d4', bgColor: 'rgba(6, 182, 212, 0.15)' },
  form: { icon: FileText, color: '#10b981', bgColor: 'rgba(16, 185, 129, 0.15)' },
  event: { icon: Zap, color: '#f59e0b', bgColor: 'rgba(245, 158, 11, 0.15)' },
}

interface TriggerShapeNodeProps {
  data: TriggerShapeNodeData
  selected?: boolean
}

function TriggerShapeNodeComponent({ data, selected }: TriggerShapeNodeProps) {
  const { label, triggerType, status = 'idle', scheduleText } = data
  const config = triggerConfig[triggerType] || triggerConfig.manual
  const Icon = config.icon

  return (
    <div
      className={cn(
        'relative group',
        'transition-all duration-200'
      )}
      style={{ width: 180, height: 80 }}
    >
      {/* Rounded pill/stadium shape - common for start nodes */}
      <svg
        viewBox="0 0 180 80"
        className="absolute inset-0 w-full h-full"
        style={{
          filter: selected
            ? `drop-shadow(0 0 12px ${config.color}50)`
            : 'drop-shadow(0 4px 6px rgba(0,0,0,0.07))',
        }}
      >
        <defs>
          <linearGradient id={`trigger-grad-${triggerType}`} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={config.color} stopOpacity="0.2" />
            <stop offset="100%" stopColor={config.color} stopOpacity="0.05" />
          </linearGradient>
        </defs>
        {/* Stadium/pill shape */}
        <rect
          x="2"
          y="2"
          width="176"
          height="76"
          rx="38"
          ry="38"
          fill={`url(#trigger-grad-${triggerType})`}
          stroke={selected ? config.color : '#e5e7eb'}
          strokeWidth={selected ? 2.5 : 1.5}
          className="dark:stroke-gray-600"
        />
        {/* Inner accent line */}
        <rect
          x="8"
          y="8"
          width="164"
          height="64"
          rx="32"
          ry="32"
          fill="none"
          stroke={config.color}
          strokeWidth="1"
          strokeOpacity="0.3"
          strokeDasharray="4 4"
        />
      </svg>

      {/* Content */}
      <div className="absolute inset-0 flex items-center justify-center gap-3 px-4">
        <div
          className="flex-shrink-0 p-2.5 rounded-full"
          style={{ backgroundColor: config.bgColor }}
        >
          <Icon className="w-5 h-5" style={{ color: config.color }} />
        </div>
        <div className="min-w-0">
          <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
            {label}
          </p>
          {scheduleText && (
            <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
              {scheduleText}
            </p>
          )}
        </div>
      </div>

      {/* Status indicator */}
      {status === 'active' && (
        <div className="absolute top-1 right-3">
          <span className="relative flex h-2.5 w-2.5">
            <span
              className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75"
              style={{ backgroundColor: config.color }}
            />
            <span
              className="relative inline-flex rounded-full h-2.5 w-2.5"
              style={{ backgroundColor: config.color }}
            />
          </span>
        </div>
      )}

      {/* Source Handle - only output for triggers */}
      <Handle
        type="source"
        position={Position.Bottom}
        className="!w-3 !h-3 !border-2 !border-white dark:!border-gray-800 !-bottom-1.5"
        style={{ backgroundColor: config.color }}
      />
    </div>
  )
}

export const TriggerShapeNode = memo(TriggerShapeNodeComponent)
export default TriggerShapeNode
